import type {
  Child,
  Guest,
  MealPreference,
  WeddingCoupleRole,
  WitnessRole,
} from "@amazing-wedding/shared-types";
import i18n from "@/lib/i18n";

/** Legacy seeded placeholder names before `weddingRole` existed. */
export function isLegacyGroomName(guest: Pick<Guest, "firstName" | "lastName">): boolean {
  return guest.firstName.trim() === "Pan" && guest.lastName.trim() === "Młody";
}

export function isLegacyBrideName(guest: Pick<Guest, "firstName" | "lastName">): boolean {
  return guest.firstName.trim() === "Pani" && guest.lastName.trim() === "Młoda";
}

/** Returns explicit or legacy-inferred wedding couple role. */
export function resolveWeddingRole(guest: Guest): WeddingCoupleRole | null {
  if (guest.weddingRole === "groom" || guest.weddingRole === "bride") {
    return guest.weddingRole;
  }
  if (isLegacyGroomName(guest)) return "groom";
  if (isLegacyBrideName(guest)) return "bride";
  return null;
}

export function isWeddingCoupleGuest(guest: Guest): boolean {
  return resolveWeddingRole(guest) !== null;
}

export function isWeddingCouplePair(a: Guest, b: Guest): boolean {
  const roleA = resolveWeddingRole(a);
  const roleB = resolveWeddingRole(b);
  if (!roleA || !roleB || roleA === roleB) return false;
  return a.partnerId === b.id && b.partnerId === a.id;
}

/** Groom first, then bride — canonical pair only. */
export function getAllWeddingRoleGuests(allGuests: Guest[], role: WeddingCoupleRole): Guest[] {
  return allGuests.filter((g) => resolveWeddingRole(g) === role);
}

function compareGuestCreatedAt(a: Guest, b: Guest): number {
  return a.createdAt.localeCompare(b.createdAt);
}

/** Pick the single authoritative groom + bride from duplicates. */
export function pickCanonicalCouplePair(allGuests: Guest[]): {
  groom: Guest | null;
  bride: Guest | null;
} {
  const grooms = getAllWeddingRoleGuests(allGuests, "groom");
  const brides = getAllWeddingRoleGuests(allGuests, "bride");

  for (const groom of [...grooms].sort(compareGuestCreatedAt)) {
    for (const bride of [...brides].sort(compareGuestCreatedAt)) {
      if (groom.partnerId === bride.id && bride.partnerId === groom.id) {
        return { groom, bride };
      }
    }
  }

  const pickOne = (candidates: Guest[], role: WeddingCoupleRole): Guest | null => {
    if (candidates.length === 0) return null;
    const explicit = candidates.filter((g) => g.weddingRole === role);
    const pool = explicit.length > 0 ? explicit : candidates;
    return [...pool].sort(compareGuestCreatedAt)[0] ?? null;
  };

  return { groom: pickOne(grooms, "groom"), bride: pickOne(brides, "bride") };
}

export function getCanonicalCoupleIds(allGuests: Guest[]): Set<string> {
  const { groom, bride } = pickCanonicalCouplePair(allGuests);
  const ids = new Set<string>();
  if (groom) ids.add(groom.id);
  if (bride) ids.add(bride.id);
  return ids;
}

/** True when guest has a wedding role but is not the canonical groom/bride doc. */
export function isDuplicateWeddingCoupleGuest(guest: Guest, allGuests: Guest[]): boolean {
  if (!isWeddingCoupleGuest(guest)) return false;
  return !getCanonicalCoupleIds(allGuests).has(guest.id);
}

export function systemCoupleRoleLabel(role: WeddingCoupleRole): string {
  return role === "groom" ? i18n.t("guests.role.groom") : i18n.t("guests.role.bride");
}

export function witnessRoleLabel(role: WitnessRole): string {
  return role === "witnessM" ? i18n.t("guests.role.witnessM") : i18n.t("guests.role.witnessF");
}

/** Legacy seeded placeholder surnames on groom/bride guest docs — never shown in UI. */
export function isCouplePlaceholderLastName(lastName: string): boolean {
  const last = lastName.trim();
  return last === "Młody" || last === "Młoda";
}

/** First name only when placeholder surname was stored on the couple guest doc. */
export function stripCouplePlaceholderLastName(
  guest: Pick<Guest, "firstName" | "lastName">,
): string {
  const first = guest.firstName.trim();
  const last = guest.lastName.trim();
  if (isCouplePlaceholderLastName(last)) return first;
  return [first, last].filter(Boolean).join(" ");
}

function systemCoupleFallbackDisplayName(guest: Guest): string {
  return stripCouplePlaceholderLastName(guest) || hostDisplayName(guest);
}

/** Host row label — respects wedding settings and hides placeholder couple surnames. */
export function guestHostDisplayName(
  guest: Guest,
  wedding?: { coupleName1?: string; coupleName2?: string | null } | null,
): string {
  if (isWeddingCoupleGuest(guest)) {
    return systemCoupleDisplayName(guest, wedding);
  }
  return hostDisplayName(guest);
}

export function systemCoupleDisplayName(
  guest: Guest,
  wedding?: { coupleName1?: string; coupleName2?: string | null } | null,
): string {
  const role = resolveWeddingRole(guest);
  if (role === "groom") {
    const fromSettings = wedding?.coupleName1?.trim();
    if (fromSettings) return fromSettings;
    return systemCoupleFallbackDisplayName(guest);
  }
  if (role === "bride") {
    const fromSettings = wedding?.coupleName2?.trim();
    if (fromSettings) return fromSettings;
    return systemCoupleFallbackDisplayName(guest);
  }
  return hostDisplayName(guest);
}

/** Groom first, then bride — only the canonical system couple docs. */
export function getWeddingCoupleGuests(allGuests: Guest[]): Guest[] {
  const { groom, bride } = pickCanonicalCouplePair(allGuests);
  return [groom, bride].filter((g): g is Guest => g != null);
}

export function partitionWeddingCouple(guests: Guest[]): { couple: Guest[]; rest: Guest[] } {
  const coupleIds = new Set(getWeddingCoupleGuests(guests).map((g) => g.id));
  const couple = getWeddingCoupleGuests(guests);
  const rest = guests.filter((g) => !coupleIds.has(g.id));
  return { couple, rest };
}

/** Partner of a canonical groom/bride guest, or null when not part of the couple. */
export function getPartnerForCouple(guest: Guest, allGuests: Guest[]): Guest | null {
  const { groom, bride } = pickCanonicalCouplePair(allGuests);
  if (groom && guest.id === groom.id) return bride;
  if (bride && guest.id === bride.id) return groom;
  return null;
}

/** Guest plus canonical partner when applicable — otherwise a single-member list. */
export function coupleMembersForGuest(guest: Guest, allGuests: Guest[]): Guest[] {
  const partner = getPartnerForCouple(guest, allGuests);
  return partner ? [guest, partner] : [guest];
}

/** Canonical groom + bride when `guest` is one of them. */
export function getCouplePairForGuest(
  guest: Guest,
  allGuests: Guest[],
): { groom: Guest; bride: Guest } | null {
  const partner = getPartnerForCouple(guest, allGuests);
  if (!partner) return null;
  const role = resolveWeddingRole(guest);
  if (role === "groom") return { groom: guest, bride: partner };
  if (role === "bride") return { groom: partner, bride: guest };
  return null;
}

/** Pin groom + bride at the top; couple rows stay visible even when filters hide them. */
export function pinWeddingCoupleFirst(sorted: Guest[], allGuests: Guest[]): Guest[] {
  const couple = getWeddingCoupleGuests(allGuests);
  const coupleIds = new Set(couple.map((g) => g.id));
  const rest = sorted.filter((g) => !coupleIds.has(g.id));
  return [...couple, ...rest];
}

export type GuestUnitRow =
  | { kind: "host"; key: string; guest: Guest }
  | { kind: "plusOne"; key: string; guest: Guest }
  | { kind: "child"; key: string; guest: Guest; child: Child };

/** Position of a row within its invitation unit block (for household chrome). */
export type UnitPosition = "solo" | "first" | "middle" | "last";

/** Position within a couple pair (two peers at the same indent level). */
export type CouplePosition = "coupleFirst" | "coupleLast";

export type GuestUnitRowWithMeta = GuestUnitRow & {
  unitPosition: UnitPosition;
  unitSize: number;
  /** Present on host rows that are part of a couple — id of the partner guest. */
  coupleWithGuestId?: string;
  /** Visual position within the couple block. */
  couplePosition?: CouplePosition;
  /** Rows spanned by shared group/RSVP/nocleg on the anchor row (desktop layout). */
  sharedFieldSpanRows?: number;
};

/** Mutual partnerId pairs that are not the groom/bride system guests. */
export function findStalePartnerPairIds(allGuests: Guest[]): Array<[string, string]> {
  const pairs: Array<[string, string]> = [];
  const seen = new Set<string>();
  const byId = new Map(allGuests.map((g) => [g.id, g]));

  for (const g of allGuests) {
    if (!g.partnerId) continue;
    const partner = byId.get(g.partnerId);
    if (!partner || partner.partnerId !== g.id) continue;
    const key = [g.id, partner.id].sort().join(":");
    if (seen.has(key)) continue;
    seen.add(key);
    if (!isWeddingCouplePair(g, partner)) {
      pairs.push([g.id, partner.id]);
    }
  }
  return pairs;
}

export function hasHouseholdMembers(g: Guest): boolean {
  // plusOne with empty firstName is anonymous — still a member
  return (g.plusOne !== null && g.plusOne !== undefined) || (g.children?.length ?? 0) > 0;
}

/**
 * Build a Map<guestId → partnerId> containing only *confirmed* couples —
 * i.e. pairs where both guests reference each other symmetrically.
 * One-sided references (data inconsistency) are excluded.
 */
export function buildPartnerMap(guests: Guest[]): Map<string, string> {
  const map = new Map<string, string>();
  const byId = new Map(guests.map((g) => [g.id, g]));
  for (const g of guests) {
    if (!g.partnerId) continue;
    const partner = byId.get(g.partnerId);
    if (partner?.partnerId === g.id) {
      map.set(g.id, g.partnerId);
    }
  }
  return map;
}

export function isUnitMuted(guest: Guest): boolean {
  if (isWeddingCoupleGuest(guest)) return false;
  return guest.rsvpStatus !== "accepted";
}

export function householdAccentColour(groupColour: string): string {
  return `color-mix(in srgb, ${groupColour} 40%, var(--line))`;
}

/** Resting wash for multi-row household units when the host belongs to a group. */
export function householdRowBackground(groupColour: string): string {
  return `color-mix(in srgb, ${groupColour} 12%, var(--paper))`;
}

/** Slightly stronger wash on hover/focus-within for grouped household units. */
export function householdRowBackgroundHover(groupColour: string): string {
  return `color-mix(in srgb, ${groupColour} 18%, var(--bg-2))`;
}

/** Whether a list row should show the group colour wash (solo/couple hosts + household members). */
export function guestRowUsesGroupColour(
  rowKind: GuestUnitRow["kind"],
  unitPosition: UnitPosition,
  hasGroupId: boolean,
): boolean {
  if (!hasGroupId) return false;
  if (unitPosition !== "solo") return true;
  return rowKind === "host";
}

/** Normalized first|last key — same convention as findDuplicate in filters.ts. */
export function personNameKey(firstName: string, lastName: string): string {
  return `${firstName.trim().toLowerCase()}|${lastName.trim().toLowerCase()}`;
}

/** True when a wedding-couple host's plusOne slot duplicates the canonical partner. */
function plusOneRefersToCanonicalPartner(guest: Guest, allGuests: Guest[]): boolean {
  const plusOne = guest.plusOne;
  if (!plusOne || !isWeddingCoupleGuest(guest)) return false;
  const partner = getPartnerForCouple(guest, allGuests);
  if (!partner) return false;

  const linkedId = plusOne.guestId?.trim();
  if (linkedId) return linkedId === partner.id;

  if (!plusOne.firstName?.trim()) return false;
  return (
    personNameKey(plusOne.firstName, plusOne.lastName ?? "") ===
    personNameKey(partner.firstName, partner.lastName)
  );
}

/** Guest ids referenced as someone else's linked companion or child. */
export function collectCompanionGuestIds(guests: Guest[]): Set<string> {
  const ids = new Set<string>();
  const canonicalCoupleIds = getCanonicalCoupleIds(guests);
  for (const g of guests) {
    const plusOneId = g.plusOne?.guestId?.trim();
    if (plusOneId) {
      if (isWeddingCoupleGuest(g) && canonicalCoupleIds.has(plusOneId)) continue;
      ids.add(plusOneId);
    }
    for (const c of g.children ?? []) {
      const childId = c.guestId?.trim();
      if (childId) ids.add(childId);
    }
  }
  return ids;
}

function collectLegacyCompanionNameKeys(guests: Guest[]): Set<string> {
  const keys = new Set<string>();
  for (const g of guests) {
    const p = g.plusOne;
    if (!p?.firstName?.trim() || p.guestId?.trim()) continue;
    keys.add(personNameKey(p.firstName, p.lastName ?? ""));
  }
  return keys;
}

function collectLegacyChildFirstNameKeys(guests: Guest[]): Set<string> {
  const keys = new Set<string>();
  for (const g of guests) {
    for (const c of g.children ?? []) {
      if (!c.firstName?.trim() || c.guestId?.trim()) continue;
      keys.add(c.firstName.trim().toLowerCase());
    }
  }
  return keys;
}

export type CompanionDedupContext = {
  companionIds: Set<string>;
  legacyNameKeys: Set<string>;
  legacyChildFirstNameKeys: Set<string>;
};

export function companionDedupContext(allGuests: Guest[]): CompanionDedupContext {
  return {
    companionIds: collectCompanionGuestIds(allGuests),
    legacyNameKeys: collectLegacyCompanionNameKeys(allGuests),
    legacyChildFirstNameKeys: collectLegacyChildFirstNameKeys(allGuests),
  };
}

function countGuestsWithNameKey(guests: Guest[], key: string): number {
  return guests.filter((g) => personNameKey(g.firstName, g.lastName) === key).length;
}

function countGuestsWithFirstName(guests: Guest[], firstName: string): number {
  const key = firstName.trim().toLowerCase();
  return guests.filter((g) => g.firstName.trim().toLowerCase() === key).length;
}

/** True when this guest should appear only as a companion/child sub-row, not as a host. */
export function isGuestShownAsCompanion(
  guest: Guest,
  allGuests: Guest[],
  companionIds = collectCompanionGuestIds(allGuests),
  legacyNameKeys = collectLegacyCompanionNameKeys(allGuests),
  legacyChildFirstNameKeys = collectLegacyChildFirstNameKeys(allGuests),
): boolean {
  if (getCanonicalCoupleIds(allGuests).has(guest.id)) return false;
  if (companionIds.has(guest.id)) return true;

  const key = personNameKey(guest.firstName, guest.lastName);
  if (legacyNameKeys.has(key) && countGuestsWithNameKey(allGuests, key) === 1) {
    for (const host of allGuests) {
      if (!plusOneRefersToCanonicalPartner(host, allGuests)) continue;
      const partner = getPartnerForCouple(host, allGuests);
      if (partner?.id === guest.id) return false;
    }
    return true;
  }

  const childFirst = guest.firstName.trim().toLowerCase();
  if (
    legacyChildFirstNameKeys.has(childFirst) &&
    countGuestsWithFirstName(allGuests, childFirst) === 1
  ) {
    return true;
  }

  return false;
}

/** Guest docs that should produce a standalone host row in the list. */
export function guestsForHostRows(allGuests: Guest[]): Guest[] {
  const ctx = companionDedupContext(allGuests);
  const canonicalCoupleIds = getCanonicalCoupleIds(allGuests);
  return allGuests.filter(
    (g) =>
      !(isWeddingCoupleGuest(g) && !canonicalCoupleIds.has(g.id)) &&
      !isGuestShownAsCompanion(
        g,
        allGuests,
        ctx.companionIds,
        ctx.legacyNameKeys,
        ctx.legacyChildFirstNameKeys,
      ),
  );
}

/** One list row per person: host, then companion, then each child. */
export function flattenGuestUnits(guests: Guest[], allGuests?: Guest[]): GuestUnitRow[] {
  const full = allGuests ?? guests;
  const ctx = companionDedupContext(full);
  const partnerMap = buildPartnerMap(full);

  // Build rows in input order, tracking which guest IDs have already been emitted
  // so that a couple partner isn't emitted twice.
  const emitted = new Set<string>();
  const rows: GuestUnitRow[] = [];

  const emitGuestUnit = (g: Guest) => {
    if (emitted.has(g.id)) return;
    emitted.add(g.id);

    if (
      !isGuestShownAsCompanion(
        g,
        full,
        ctx.companionIds,
        ctx.legacyNameKeys,
        ctx.legacyChildFirstNameKeys,
      )
    ) {
      rows.push({ kind: "host", key: `host:${g.id}`, guest: g });
    }
    if (g.plusOne !== null && g.plusOne !== undefined) {
      if (!plusOneRefersToCanonicalPartner(g, full)) {
        rows.push({ kind: "plusOne", key: `plusOne:${g.id}`, guest: g });
      }
    }
    for (const c of g.children ?? []) {
      if (!c.firstName?.trim()) continue;
      rows.push({ kind: "child", key: `child:${g.id}:${c.id}`, guest: g, child: c });
    }
  };

  const guestById = new Map(full.map((g) => [g.id, g]));

  for (const g of guests) {
    if (emitted.has(g.id)) continue;
    emitGuestUnit(g);

    // If this guest has a confirmed partner and the partner is in the visible
    // set, emit the partner's unit immediately after so they stay adjacent.
    const partnerId = partnerMap.get(g.id);
    if (partnerId) {
      const partner = guestById.get(partnerId);
      if (partner && guests.includes(partner)) {
        emitGuestUnit(partner);
      }
    }
  }

  return rows;
}

/** Attach unit-block position metadata for visual grouping across consecutive rows. */
export function annotateGuestUnitRows(rows: GuestUnitRow[]): GuestUnitRowWithMeta[] {
  // Pass 1: compute household unit position (same as before).
  const pass1: GuestUnitRowWithMeta[] = [];
  let i = 0;
  while (i < rows.length) {
    const first = rows[i];
    if (!first) break;
    const guestId = first.guest.id;
    let j = i + 1;
    while (j < rows.length && rows[j]?.guest.id === guestId) j++;
    const unitSize = j - i;
    for (let k = i; k < j; k++) {
      const row = rows[k];
      if (!row) continue;
      const posInUnit = k - i;
      const unitPosition: UnitPosition =
        unitSize === 1
          ? "solo"
          : posInUnit === 0
            ? "first"
            : posInUnit === unitSize - 1
              ? "last"
              : "middle";
      pass1.push({ ...row, unitPosition, unitSize });
    }
    i = j;
  }

  // Pass 2: detect adjacent couple pairs and annotate them.
  // A couple pair is two consecutive host rows where guest A's partnerId === B.id
  // and B's partnerId === A.id (symmetric, confirmed by buildPartnerMap).
  const result: GuestUnitRowWithMeta[] = [];
  let p = 0;
  while (p < pass1.length) {
    const current = pass1[p]!;
    const next = pass1[p + 1];

    // Check if `current` is a host-row "first" or "solo" in its unit, and
    // `next` is a host-row "first" or "solo" in a *different* guest unit,
    // and they are mutual partners.
    const currentIsHostUnit =
      current.kind === "host" &&
      (current.unitPosition === "solo" || current.unitPosition === "first");
    const nextIsHostUnit =
      next &&
      next.kind === "host" &&
      (next.unitPosition === "solo" || next.unitPosition === "first") &&
      next.guest.id !== current.guest.id;
    const areMutualPartners =
      currentIsHostUnit &&
      nextIsHostUnit &&
      current.guest.partnerId === next.guest.id &&
      next.guest.partnerId === current.guest.id;

    if (areMutualPartners && isWeddingCouplePair(current.guest, next.guest)) {
      // Collect all rows belonging to the first guest's unit and the second guest's unit.
      const firstUnitSize = current.unitSize;
      const secondUnitSize = next.unitSize;

      // Emit first guest unit annotated as coupleFirst
      const coupleBlockRows = firstUnitSize + secondUnitSize;
      for (let k = 0; k < firstUnitSize; k++) {
        const row = pass1[p + k]!;
        result.push({
          ...row,
          coupleWithGuestId: next.guest.id,
          couplePosition: "coupleFirst",
          ...(k === 0 && row.kind === "host" ? { sharedFieldSpanRows: coupleBlockRows } : {}),
        });
      }
      // Emit second guest unit annotated as coupleLast
      for (let k = 0; k < secondUnitSize; k++) {
        const row = pass1[p + firstUnitSize + k]!;
        result.push({
          ...row,
          coupleWithGuestId: current.guest.id,
          couplePosition: "coupleLast",
        });
      }
      p += firstUnitSize + secondUnitSize;
    } else {
      const sharedFieldSpanRows =
        current.unitSize > 1 && current.unitPosition === "first" && current.kind === "host"
          ? current.unitSize
          : undefined;
      result.push(sharedFieldSpanRows ? { ...current, sharedFieldSpanRows } : current);
      p++;
    }
  }

  return result;
}

/** Host(s) that reference this guest as a linked or legacy companion/child. */
export function findHostsForCompanion(companion: Guest, allGuests: Guest[]): Guest[] {
  const hosts: Guest[] = [];
  const seen = new Set<string>();

  for (const g of allGuests) {
    if (g.plusOne?.guestId?.trim() === companion.id) {
      if (!seen.has(g.id)) {
        seen.add(g.id);
        hosts.push(g);
      }
    }
    for (const c of g.children ?? []) {
      if (c.guestId?.trim() === companion.id && !seen.has(g.id)) {
        seen.add(g.id);
        hosts.push(g);
      }
    }
  }
  if (hosts.length > 0) return hosts;

  const nameKey = personNameKey(companion.firstName, companion.lastName);
  for (const g of allGuests) {
    const p = g.plusOne;
    if (!p?.firstName?.trim() || p.guestId?.trim()) continue;
    if (personNameKey(p.firstName, p.lastName ?? "") !== nameKey) continue;
    if (!seen.has(g.id)) {
      seen.add(g.id);
      hosts.push(g);
    }
  }

  const childFirst = companion.firstName.trim().toLowerCase();
  if (countGuestsWithFirstName(allGuests, childFirst) === 1) {
    for (const g of allGuests) {
      for (const c of g.children ?? []) {
        if (c.guestId?.trim() || c.firstName.trim().toLowerCase() !== childFirst) continue;
        if (!seen.has(g.id)) {
          seen.add(g.id);
          hosts.push(g);
        }
      }
    }
  }

  return hosts;
}

/** First host for a companion, if any. */
export function findHostForCompanion(companion: Guest, allGuests: Guest[]): Guest | undefined {
  return findHostsForCompanion(companion, allGuests)[0];
}

export type LegacyLinkTarget =
  | { hostId: string; kind: "plusOne" }
  | { hostId: string; kind: "child"; childId: string };

/** Host rows whose legacy plus-one/child name matches a newly created guest. */
export function findLegacyLinkTargets(
  newGuest: Pick<Guest, "id" | "firstName" | "lastName">,
  allGuests: Guest[],
): LegacyLinkTarget[] {
  const targets: LegacyLinkTarget[] = [];
  const nameKey = personNameKey(newGuest.firstName, newGuest.lastName);
  const childFirst = newGuest.firstName.trim().toLowerCase();
  // Defensive: callers may pass `allGuests` either with or without the new
  // guest itself. Count only OTHER guests sharing the first name so the
  // "ambiguous → don't link" rule applies consistently in both shapes.
  const otherWithSameFirstName = allGuests.filter(
    (g) => g.id !== newGuest.id && g.firstName.trim().toLowerCase() === childFirst,
  ).length;
  const uniqueChildName = otherWithSameFirstName === 0;

  for (const host of allGuests) {
    if (host.id === newGuest.id) continue;
    const p = host.plusOne;
    if (
      p?.firstName?.trim() &&
      !p.guestId?.trim() &&
      personNameKey(p.firstName, p.lastName ?? "") === nameKey
    ) {
      targets.push({ hostId: host.id, kind: "plusOne" });
    }
    for (const c of host.children ?? []) {
      if (c.guestId?.trim()) continue;
      if (uniqueChildName && c.firstName.trim().toLowerCase() === childFirst) {
        targets.push({ hostId: host.id, kind: "child", childId: c.id });
      }
    }
  }
  return targets;
}

export function plusOneDisplayName(g: Guest): string {
  const p = g.plusOne;
  if (!p) return "";
  const fn = p.firstName.trim();
  const ln = (p.lastName ?? "").trim();
  const name = [fn, ln].filter(Boolean).join(" ");
  // Empty firstName → anonymous companion
  return name || i18n.t("guests.row.plusOne");
}

export function hostDisplayName(g: Guest): string {
  return `${g.firstName} ${g.lastName}`.trim();
}

export function childFullName(child: Child): string {
  return [child.firstName.trim(), (child.lastName ?? "").trim()].filter(Boolean).join(" ");
}

export function childDisplayName(child: Child): string {
  const name = childFullName(child) || child.firstName.trim();
  if (child.age != null) return i18n.t("guests.childAge", { name, age: child.age });
  return name;
}

/** Companion + child labels for compact guest-row subtitles (seating sidebar, drag chip). */
export function guestHouseholdMemberLabels(g: Guest): string[] {
  const labels: string[] = [];
  if (g.plusOne != null) {
    labels.push(plusOneDisplayName(g));
  }
  for (const child of g.children ?? []) {
    if (child.firstName?.trim()) {
      labels.push(childDisplayName(child));
    }
  }
  return labels;
}

export function mealPreferenceLabel(p: MealPreference | null | undefined): string {
  if (!p) return i18n.t("guests.meal.unset");
  return i18n.t(`guests.meal.${p}`);
}
